import { useState } from 'react';

import type { Nullable } from '@repo/common';
import { DataTestIds, type Picture, type PictureInput } from '@repo/frontend-shared';
import { SDSAvatar } from '@repo/sds/SDSAvatar';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';

import { UploadPictureModal } from '../UploadPictureModal';

interface UserProfileHeaderProps {
  profileName: string;
  loading: boolean;
  picture: Nullable<Partial<Picture>>;
  onSave: (payload: PictureInput) => void | Promise<void>;
  onDelete: () => void;
  removeButtonText?: string;
}

export function UserProfileHeader({
  profileName,
  picture,
  onSave,
  onDelete,
  loading,
  removeButtonText = 'Remove',
}: UserProfileHeaderProps) {
  const [modalOpened, setModalOpened] = useState(false);

  const handleOpenModal = () => {
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setModalOpened(false);
  };

  return (
    <div>
      <SDSFlexbox direction="column" gap="text-x-large">
        <SDSFlexbox gap="text-medium" alignItems="center">
          <SDSAvatar
            dataTestId={DataTestIds.PROFILE.USER_AVATAR}
            src={picture?.thumbnailUrl}
            alt="Profile"
            name={profileName}
            size="large"
          />
          <SDSText type="h5" text={profileName} />
        </SDSFlexbox>
        <SDSFlexbox gap="button-regular">
          <SDSButton
            dataTestId={DataTestIds.PROFILE.IMAGE_UPLOAD_BTN}
            type="button"
            text={picture?.originalUrl ? 'Re-Upload' : 'Upload'}
            mode="outline"
            textType="button-medium"
            size="small"
            isFullWidth
            onClick={handleOpenModal}
            isSubmitting={loading}
            ariaLabel={picture?.originalUrl ? 'Re-upload profile picture' : 'Upload profile picture'}
            ariaBusy={loading}
          />
          <SDSButton
            dataTestId={DataTestIds.PROFILE.IMAGE_REMOVE_BTN}
            type="button"
            status="alert"
            text={removeButtonText}
            mode="outline"
            textType="button-medium"
            size="small"
            isDisabled={!picture?.originalUrl}
            isFullWidth
            onClick={onDelete}
            isSubmitting={!!picture?.originalUrl && loading}
            ariaLabel="Remove profile picture"
            ariaBusy={!!picture?.originalUrl && loading}
          />
        </SDSFlexbox>
        <SDSSeparator />
      </SDSFlexbox>
      <UploadPictureModal
        isOpen={modalOpened}
        onClose={handleCloseModal}
        picture={picture}
        onSave={onSave}
        isPersisting={loading}
      />
    </div>
  );
}
